import React, { useState, useEffect } from "react";
import { Link, useHistory, useLocation, useRouteMatch } from "react-router-dom";
import {
  Collapse,
  Navbar,
  NavbarToggler,
  NavbarBrand,
  Nav,
  NavItem,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "reactstrap";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuth } from "../context/auth";
import { useThemeState } from "../context/theme";
import { useModalsDispatch, OPEN_REFERRAL } from "../context/modals";
import { getDisplayName } from "../utils";
import { useProfile } from "../api/hooks/profile";

import SupportModal from "./SupportModal";
import RedeemCouponModal from "./RedeemCouponModal";
import DocumentModal from "./DocumentModal";
import TermsAndConditions from "./TermsAndConditions";
import PrivacyPolicy from "./PrivacyPolicy";
import LoginForm from "./LoginForm";
import SignUpFormContainer from "./SignUpForm";
import Avatar from "./Avatar";
import { SET_SHIELD_TYPE } from "../context/editor";
import ForgotPasswordFormContainer from "./ForgotPasswordForm";
import FileSendIcon from "../images/pdf/file-send.svg";

export function NavLink({
  className,
  to,
  children,
  inactiveClassName = "text-white",
  ...rest
}) {
  const location = useLocation();
  const match = useRouteMatch(to);

  const isActive = to === "/" ? location.pathname === to : !!match;

  return (
    <Link
      className={clsx(
        className,
        "navbar-height border-thick d-flex align-items-center px-4 ",
        isActive && "text-accent border-bottom border-accent",
        !isActive && inactiveClassName
      )}
      to={to}
      {...rest}
    >
      {children}
    </Link>
  );
}

function NavbarDashboard({ banner, onLoginSuccess, onSignUpSuccess }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [isRedeemCouponOpen, setIsRedeemCouponOpen] = useState(false);
  const [auth, setAuth] = useAuth();
  const theme = useThemeState();
  const modalsDispatch = useModalsDispatch();
  const { profile, can } = useProfile();
  const location = useLocation();

  const history = useHistory();
  const dispatch = useModalsDispatch();
  const { t } = useTranslation("common");

  const toggle = () => setIsOpen(!isOpen);
  const logout = () => {
    setAuth({});
    if (location.pathname.includes("public-templates")) return;
    history.push("/login");
  };

  const openGeneralConfiguration = () => {
    // dispatch({ type: OPEN_GENERAL })
    history.push("/profile");
  };

  const openReferral = () => {
    modalsDispatch({ type: OPEN_REFERRAL });
  };

  const openSupport = () => {
    setIsSupportOpen(true);
  };

  const closeSupport = () => {
    setIsSupportOpen(false);
  };

  const openRedeemCoupon = () => {
    setIsRedeemCouponOpen(true);
  };

  const closeRedeemCoupon = () => {
    setIsRedeemCouponOpen(false);
  };

  const toggleProfileMenu = () => {
    setIsProfileOpen((isOpen) => !isOpen);
  };

  return (
    <Navbar className="bg-primary border-0 p-0" id="dashboard" expand="md">
      <div className="align-items-center d-flex justify-content-around w-100">
        <div className="mobile-menu">
          <Link to="/">
            <NavbarBrand
              tag="img"
              src={theme.appLogo}
              className={`img-fluid navbar-logo ${theme.logoClassName}`}
              alt="Logo Trato"
            />
          </Link>
          <NavbarToggler onClick={toggle} className="float-right ml-auto">
            <FontAwesomeIcon icon="bars" className="text-light" />
          </NavbarToggler>
        </div>

        {auth?.hasAccount && (
          <Collapse isOpen={isOpen} navbar hidden={!auth?.hasAccount}>
            <Nav className="ml-auto d-flex" navbar>
              <NavItem className="d-flex ml-4">
                <NavLink to="/" className="align-self-center">
                  {t("home")}
                </NavLink>
              </NavItem>
              {can("CREATE_DYNAMIC_CONTRACT") && (
                <NavItem className="d-flex ml-4">
                  <NavLink
                    to="/contracts/info/create"
                    className="align-self-center"
                  >
                    {t("Crear contrato")}
                  </NavLink>
                </NavItem>
              )}

              <NavItem className="d-flex ml-4">
                <NavLink to="/documents" className="align-self-center">
                  {t("contracts")}
                </NavLink>
              </NavItem>
              <NavItem className="d-flex ml-4">
                <NavLink to="/templates" className="align-self-center">
                  {t("template", { count: 2 })}
                </NavLink>
              </NavItem>
              <NavItem className="d-flex ml-4">
                <NavLink to="/plans" className="align-self-center">
                  {t("Plans.title", { count: 2 })}
                </NavLink>
              </NavItem>
              {/* <NavItem className="d-flex ml-4">
                <NavLink to="/services" className="align-self-center">
                  {t("services.header", { count: 2 })}
                </NavLink>
              </NavItem> */}
            </Nav>
          </Collapse>
        )}
        {auth?.hasAccount && (
          <Collapse isOpen={isOpen} navbar hidden={!auth?.hasAccount}>
            <Nav className="ml-auto d-flex" navbar>
              <Dropdown
                isOpen={isProfileOpen}
                className="d-flex align-items-center align-self-center ml-4"
                toggle={toggleProfileMenu}
                nav
                inNavbar
              >
                <Avatar
                  className="bg-accent-100"
                  name={profile.givenName}
                  lastName={profile.familyName}
                  onClick={toggleProfileMenu}
                />
                <DropdownToggle nav caret className="text-white">
                  {getDisplayName(profile.givenName, profile.familyName)}
                </DropdownToggle>
                <DropdownMenu right>
                  <DropdownItem onClick={openGeneralConfiguration}>
                    <FontAwesomeIcon className="text-primary mr-2" icon="cog" />
                    {t("settings")}
                  </DropdownItem>
                  <DropdownItem onClick={openReferral}>
                    <FontAwesomeIcon
                      className="text-primary mr-2"
                      icon="user-plus"
                    />{" "}
                    {t("actions.invite")}{" "}
                  </DropdownItem>
                  <DropdownItem>
                    <a
                      href="https://trato.io/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-dark"
                    >
                      <FontAwesomeIcon
                        className="text-primary mr-2"
                        icon="external-link-alt"
                      />{" "}
                      Trato.io
                    </a>
                  </DropdownItem>
                  <DropdownItem>
                    <FontAwesomeIcon
                      className="text-primary mr-2"
                      icon="video"
                    />{" "}
                    <Link to="/tutorials" className="text-dark">
                      {t("video_tutorials")}
                    </Link>
                  </DropdownItem>
                  <DropdownItem onClick={openSupport}>
                    <SupportModal
                      isOpen={isSupportOpen}
                      onClose={closeSupport}
                    />
                  </DropdownItem>
                  <DropdownItem onClick={openRedeemCoupon}>
                    <RedeemCouponModal
                      isOpen={isRedeemCouponOpen}
                      onClose={closeRedeemCoupon}
                    />
                  </DropdownItem>
                  <DropdownItem>
                    <FontAwesomeIcon
                      className="text-primary mr-2"
                      icon="book"
                    />
                    <DocumentModal
                      tag="span"
                      DocumentComponent={TermsAndConditions}
                    >
                      Términos y Condiciones
                    </DocumentModal>
                  </DropdownItem>
                  <DropdownItem>
                    <FontAwesomeIcon
                      className="text-primary mr-2"
                      icon="book-reader"
                    />
                    <DocumentModal tag="span" DocumentComponent={PrivacyPolicy}>
                      Aviso de Privacidad
                    </DocumentModal>
                  </DropdownItem>
                  <DropdownItem onClick={logout}>
                    <FontAwesomeIcon
                      className="text-primary mr-2"
                      icon="sign-out-alt"
                    />
                    {t("auth.logout")}
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
              <NavItem
                className="d-flex ml-4"
                hidden={profile?.plan?.type !== "premium"}
              >
                <span className="align-self-center text-accent">
                  {t("Premium")}
                </span>
              </NavItem>
              {/* <NavItem className="d-flex ml-3">
                            <Link to="/shop" color="accent-100" className="btn btn-accent-100 animate-up align-self-center">{t('actions.buyNow')}</Link>
                        </NavItem> */}
            </Nav>
          </Collapse>
        )}

        {!auth?.hasAccount && (
          <Nav>
            {banner && (
              <NavItem className="d-flex align-items-center">
                {banner.icon && (
                  <FontAwesomeIcon
                    icon={banner.icon}
                    size="sm"
                    className={`ml-1 text-${banner.color || "dark"}`}
                  />
                )}
                <small className={`ml-1 text-${banner.color || "dark"}`}>
                  {banner.message}
                </small>
              </NavItem>
            )}
            <NavItem className="d-flex ml-4">
              {/* <NavLink to="/login" className="align-self-center">{t('auth.login')}</NavLink>*/}
              <Button
                size="sm"
                color="accent-100"
                onClick={() =>
                  auth.openAuthModal({ type: "login", onLoginSuccess })
                }
              >
                {t("auth.login")}
              </Button>
            </NavItem>
            <NavItem className="d-flex ml-4">
              <Button
                size="sm"
                color="accent"
                onClick={() =>
                  auth.openAuthModal({ type: "signup", onSignUpSuccess })
                }
              >
                {t("auth.createAccount")}
              </Button>
            </NavItem>
          </Nav>
        )}
      </div>
    </Navbar>
  );
}

export default NavbarDashboard;
